import { Container, Box, Heading, Text } from '@chakra-ui/react';
import OnboardingRoles from './OnboardingRoles';
import { useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import BracketIcon from './BracketIcon';
import CodeBracketIcon from './CodeBracketIcon';
import CodeIcon from './CodeIcon';
import dots from '../../public/images/light-lp/footer/dots.svg';

const MotionBox = motion(Box);

interface Hero {
  hero: {
    title: {
      main: string;
      highlight: string;
    };
    text: string;
    italic?: string;
    button?: {
      text: string;
      url: string;
    };
  };
  theme: {
    green100?: string;
    green200?: string;
    green300?: string;
    blue100?: string;
    blue200?: string;
    blue300?: string;
    yellow100?: string;
    yellow200?: string;
    yellow300?: string;
    red100?: string;
  };
  onboardingRoles: {
    icon: string;
    title: string;
    query: string;
  }[];
  signUpUrl: string;
}

export default function HeroWithOnboarding({ hero, theme, onboardingRoles, signUpUrl }: Hero) {
  const iconAnimation = useAnimation();

  useEffect(() => {
    iconAnimation.start((i) => ({
      y: 0,
      opacity: 1,
      transition: {
        delay: i * 0.5,
        duration: 2,
        type: 'spring',
        bounce: 0.5,
      },
    }));
  }, [iconAnimation]);

  return (
    <Box
      pt={{
        base: '10',
        md: '6',
        lg: '16',
      }}
      pb={{
        base: '10',
        md: '6',
        lg: '9.75rem',
      }}
      bgImage={{ lg: `url(${dots.src})` }}
      bgPosition="calc(50% + 385px) 350px"
      bgRepeat="no-repeat"
      bgSize="15rem"
      overflow="hidden"
    >
      <Container position="relative">
        <Box mx="auto" textAlign={{ md: 'center' }}>
          <Heading
            as="h1"
            mb={{ base: 4, lg: 6 }}
            fontSize={{
              base: '3.5rem',
              lg: '4.5rem',
            }}
            lineHeight={{ base: 'shorter', lg: 'base' }}
            textAlign="center"
          >
            {hero.title.main}{' '}
            {hero.title.highlight && (
              <Box
                display={{ base: 'block', lg: 'inline-block' }}
                as="span"
                color={theme.green200}
                position="relative"
              >
                {hero.title.highlight}
              </Box>
            )}
          </Heading>
          {hero.text && (
            <Text lineHeight="shorter" fontSize={{ base: 'md' }} fontWeight="bold" mx="auto">
              {hero.text}
            </Text>
          )}
          {hero.italic && (
            <Box
              as="em"
              display="block"
              maxW={{ md: '32.5rem' }}
              mx="auto"
              fontSize={{ base: 'md' }}
              textAlign={{ base: 'left', md: 'center' }}
              mt="2"
              mb="6"
            >
              {hero.italic}
            </Box>
          )}
        </Box>
        <OnboardingRoles onboardingRoles={onboardingRoles} theme={theme} signUpUrl={signUpUrl} />
        <MotionBox
          display={{ base: 'none', lg: 'block' }}
          position="absolute"
          left="-3.125rem"
          top="5.625rem"
          zIndex="0"
          w="6.75rem"
          h="6.75rem"
          animate={iconAnimation}
          initial={{ y: -50, opacity: 0 }}
          custom={0}
        >
          <CodeBracketIcon w="100%" h="100%" />
        </MotionBox>
        <MotionBox
          display={{ base: 'none', lg: 'block' }}
          position="absolute"
          right="-3.125rem"
          top="6.25rem"
          zIndex="0"
          w="6.75rem"
          h="6.75rem"
          animate={iconAnimation}
          initial={{ y: -40, opacity: 0 }}
          custom={1}
        >
          <CodeIcon w="100%" h="100%" />
        </MotionBox>
        <MotionBox
          display={{ base: 'none', lg: 'block' }}
          position="absolute"
          left="150px"
          bottom="-7.5rem"
          zIndex="0"
          w="6.75rem"
          h="6.75rem"
          animate={iconAnimation}
          initial={{ y: -30, opacity: 0 }}
          custom={2}
        >
          <BracketIcon w="100%" h="100%" />
        </MotionBox>
      </Container>
    </Box>
  );
}
